import moment from "moment";

import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { MyleObjectType } from "@/Common/Models/Enum/MyleObjectType";
import { Guid } from "@/Common/Models/Guid";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { IPortalShareableItem } from "@/Common/Models/Portal/UI/IPortalShareableItem";
import { BillingPaymentMethod } from "@/Common/Models/UI/BillingPaymentMethod";
import { PrivateBill_BilledTo } from "@/Common/Models/UI/Enums";

class PatientPrivateBillItem implements IPortalShareableItem {
    public PrivateBillId: Guid;
    public BillDate: string;
    public BillDateForHeader: string;
    public CreatedBy: string;
    public BillNumber: number;
    public Employee: string;
    public EmployeeContactFacetId: Guid;
    public IsInvalid: boolean;

    public IsClosed: boolean;
    public IsLockedForEditing: boolean;
    public TotalBill: number;
    public CanModifyPaymentInformation: boolean;
    public SiteId: Guid;
    public SharedItem: SharedItem;
    public SharedItemMessages: SharedItemMessage[];
    public LastUpdateDate: Date;

    public RecipientName: string;
    public RecipientAddress: string;
    public RecipientPhoneNumber: string;
    public RecipientFaxNumber: string;
    public BilledTo: PrivateBill_BilledTo;
    public PaymentMethod: BillingPaymentMethod;

    public constructor(fields?: Partial<PatientPrivateBillItem>) {
        Object.assign(this, fields);
    }

    public getMyleObjectType(): MyleObjectType {
        return MyleObjectType.PrivateBill;
    }

    public getGlobalPortalStatus(): PortalSyncStatus {
        return PortalHelper.getGlobalPortalStatus(this.getSharedItem(), this.getSharedItemMessages());
    }

    public getSharedItemId(): string {
        if (this && this.SharedItem)
            return this.SharedItem.SharedItemId;
        return null;
    }

    public getSharedItem(): SharedItem {
        return this.SharedItem;
    }

    public getSharedItemMessages(): SharedItemMessage[] {
        return this.SharedItemMessages;
    }

    public getItemTypeHeader(): string {
        return "";
    }

    public getItemTitle(): string {
        return `${moment(this.BillDate).format("YYYY-MM-DD")} #${this.BillNumber}`
            .truncate(SharedItem.SubjectMaxLength);
    }

    public getDefaultSubject(): string {
        return this.getItemTitle();
    }

    public getPortalObjectId(): string {
        return this.PrivateBillId;
    }

    public getPortalLinkedItemType(): PortalLinkedItemType {
        return PortalLinkedItemType.Document;
    }

    public getSharedItemPortalStatus(): PortalSyncStatus {
        return PortalSyncStatus.Unknown;
    }

    public hasBeenModifiedSinceLastShared(): boolean {
        const sharedItem = this.getSharedItem();
        if (!sharedItem?.LastStatusDate || !this.LastUpdateDate)
            return false;

        const itemSharedDate = new Date(sharedItem?.LastStatusDate);
        const lastUpdateDate = new Date(this.LastUpdateDate);

        // trunc milliseconds because of lack of precision when rounded
        const itemSharedDateTruncated = itemSharedDate.getTime() - itemSharedDate.getMilliseconds();
        const lastUpdateDateTruncated = lastUpdateDate.getTime() - lastUpdateDate.getMilliseconds();

        return itemSharedDateTruncated < lastUpdateDateTruncated;
    }

    public hasBeenSharedOnPreviousEncounter(): boolean {
        return false;
    }
}

class PatientPrivateBillItemResponse {
    public BillNumber: number;
    public EmployeeId: Guid;
    public CreateByEmployeeId: Guid;
    public BillDate: Date;
    public PrivateBillId: Guid;
    public IsInvalid: boolean;
    public IsClosed: boolean;
    public IsLockedForEditing: boolean;
    public TotalBill: number;
    public SiteId: Guid;
    public SharedItemId: Guid;
    public SharedItem: SharedItem;
    public SharedItemMessages: SharedItemMessage[];
    public BilledTo: PrivateBill_BilledTo;
    public RecipientName: string;
    public CanModifyPaymentInformation: boolean;
    public LastUpdateDate: Date;
    public PaymentMethod: BillingPaymentMethod;
}

class PatientSelectedBillInfoResponse {
    public PrivateBillId: string;
    public PrivateBillInfo: PatientSelectedBillInfoResponseData;
    public BilledService: BilledServicesResponseData[];
}

class PatientSelectedBillInfoResponseData {
    public PatientContactFacetId: string;
    public PatientContactId: string;
    public PaymentMethod: BillingPaymentMethod;
    public GST: number;
    public QST: number;
    public HST: number;
    public GenericTax: number;
    public DiscountType: number;
    public Discount: number;
    public IsInvalid: boolean;
    public InvalidationNote: string;
    public InvalidationDate: Date;
    public InvalidatedByEmployeeId: Guid;
    public VisitDate: Date;
    public SiteId: Guid;
    public Total: number;
    public TotalGST: number;
    public TotalQST: number;
    public TotalHST: number;
    public TotalGenericTax: number;
    public Subtotal: number;
    public Notes: string;
    public IsLockedForEditing: boolean;
    public IsClosed: boolean;
    public CanModifyPaymentInformation: boolean;
    public EmployeeId: Guid;
    public CreateByEmployeeId: Guid;
    public Number: string;
    public RecipientName: string;
    public RecipientAddress: string;
    public RecipientPhoneNumber: string;
    public RecipientFaxNumber: string;
    public BilledTo: PrivateBill_BilledTo;
}

class BilledServicesResponseData {
    public Id: string;
    public Version: number;
    public CreationDate: Date;
    public CreatedBy: Guid;
    public LastUpdateDate: Date;
    public LastUpdatedBy: string;
    public DeletionDate: Date;
    public DeletedBy: Guid;
    public ArchivalDate: Date;
    public ArchivedBy: string;
    public NameFr: string;
    public NameEn: string;
    public Price: number;
    public ServiceNumber: string;
    public QST: number;
    public GST: number;
    public HST: number;
    public GenericTax: number;
    public PrivateBillId: string;
    public Multiplicator: number;
    public NameEs: string;
    public QuantityPaid: number;
    public CostCenterId: string;
    public TransfertDate: Date;
    public HadGST: boolean;
    public HadQST: boolean;
    public HadHST: boolean;
    public HadGenericTax: boolean;
}

export { PatientPrivateBillItem, PatientPrivateBillItemResponse, PatientSelectedBillInfoResponse };
